"use client"
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import {
  Filter,
  LayoutGrid,
  Grid3X3,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Instagram,
  Sparkles,
} from "lucide-react"
import Link from "next/link"
import { motion, AnimatePresence } from "framer-motion"
import * as LucideIcons from 'lucide-react'
import Image from 'next/image'

const ICON_MAP = {
  Camera: LucideIcons.Camera,
  Heart: LucideIcons.Heart,
  Users: LucideIcons.Users,
  Image: LucideIcons.Image,
  Sparkles: LucideIcons.Sparkles,
}

export default function GalleryClient({
  iconName,
  galleryName,
  description,
  images,
  tags,
}: {
  iconName: string
  galleryName: string
  description?: string
  images: Array<{ id: string; url: string; title: string; description?: string; tags: string[] }>
  tags: string[]
}) {
  const Icon = ICON_MAP[iconName as keyof typeof ICON_MAP] || LucideIcons.Image
  const [selectedTag, setSelectedTag] = useState<string>("all")
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  // Only grid layout

  useEffect(() => {
    // Optionally track page view here
  }, [])

  const filteredImages =
    selectedTag === "all"
      ? images
      : images.filter((img) => img.tags.includes(selectedTag))

  const tagCounts: Record<string, number> = {}
  images.forEach((img) => {
    img.tags.forEach((tag) => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1
    })
  })

  const selectedImageData = selectedImage ? images.find((img) => img.id === selectedImage) : null

  const openLightbox = (imageId: string) => setSelectedImage(imageId)
  const closeLightbox = () => setSelectedImage(null)
  const navigateImage = (direction: "prev" | "next") => {
    if (!selectedImage) return
    const currentIndex = filteredImages.findIndex((img) => img.id === selectedImage)
    let newIndex
    if (direction === "prev") {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1
    } else {
      newIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0
    }
    setSelectedImage(filteredImages[newIndex].id)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50">
      {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200">
              <Icon className="w-3 h-3 mr-1" />
              {galleryName} Gallery
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {galleryName}
            </h1>
            {description && (
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">{description}</p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                <Link href="/contact">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Your Session
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50 bg-transparent"
              >
                <Link href="/galleries">View All Galleries</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Controls */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              {/* Tag Filters */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedTag === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedTag("all")}
                  className={
                    selectedTag === "all"
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      : "border-purple-300 text-purple-700 hover:bg-purple-50 bg-transparent"
                  }
                >
                  <Filter className="w-3 h-3 mr-1" />
                  All Photos ({images.length})
                </Button>
                {tags.map((tag) => (
                  <Button
                    key={tag}
                    variant={selectedTag === tag ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedTag(tag)}
                    className={
                      selectedTag === tag
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                        : "border-purple-300 text-purple-700 hover:bg-purple-50 bg-transparent"
                    }
                  >
                    <Filter className="w-3 h-3 mr-1" />
                    {tag} ({tagCounts[tag] || 0})
                  </Button>
                ))}
              </div>

              {/* Layout Toggle */}
              {/* Removed Layout Toggle */}
            </div>
          </div>
        </div>
      </section>

      {/* Main Gallery */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedTag}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {filteredImages.map((image, index) => (
                  <motion.div
                    key={image.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer aspect-[4/5]"
                    onClick={() => openLightbox(image.id)}
                  >
                    <div className="aspect-[4/5] w-full h-full">
                      <Image
                        src={image.url}
                        alt={image.title}
                        width={image.width}
                        height={image.height}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-3 left-3 right-3 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="flex flex-row flex-wrap gap-2 items-end">
                        {image.tags.map((tag) => (
                          <Badge key={tag} className="bg-white/20 text-white border-white/30 text-xs">{tag}</Badge>
                        ))}
                        {image.description && (
                          <Badge className="bg-white/20 text-white border-white/30 text-xs">{image.description}</Badge>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={selectedImage !== null} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-4xl w-full h-[90vh] p-0 bg-black/95 border-0">
          {selectedImageData && (
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Close Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>

              {/* Navigation Buttons */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateImage("prev")}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateImage("next")}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>

              {/* Image */}
              <Image
                src={selectedImageData.url}
                alt={selectedImageData.title}
                width={selectedImageData.width}
                height={selectedImageData.height}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8 text-white">
            <Sparkles className="w-12 h-12 mx-auto" />
            <h2 className="text-3xl lg:text-4xl font-bold">Ready for Your Session?</h2>
            <p className="text-xl opacity-90">
              Let's create stunning portraits that celebrate your story. Each session is personalized to reflect your unique style and personality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                <Link href="/contact">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Your Session
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">
                <Link href="/galleries">View All Galleries</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
} 